import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ShoppingBag, Zap, Shield, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { StarRating } from '../components/StarRating';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1770085140346-9a2cfca85178?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHx3YXJtJTIwZGVjb3JhdGl2ZSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3NjI1OTg3NHww&ixlib=rb-4.1.0&q=85',
  festival: 'https://images.unsplash.com/photo-1703411731806-14dbf30e269f?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  decorative: 'https://images.unsplash.com/photo-1764756942041-c5db2b517eb2?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  industrial: 'https://images.unsplash.com/photo-1553465051-91f72c02edd4?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  led: 'https://images.unsplash.com/photo-1763058674379-e85fe080ce2c?crop=entropy&cs=srgb&fm=jpg&w=800&q=85'
};

export const Home = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ui, setUi] = useState(null);
  const [ratings, setRatings] = useState({});
  const [heroImgSrc, setHeroImgSrc] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, uiRes, ratingsRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/ui-controls`),
          axios.get(`${API}/products/ratings`).catch(() => ({ data: {} }))
        ]);
        setFeaturedProducts(productsRes.data.slice(0, 8));
        setUi(uiRes.data);
        setRatings(ratingsRes.data);
        // Set hero image immediately from API
        const imgUrl = uiRes.data?.heroImage;
        if (imgUrl) {
          setHeroImgSrc(imgUrl.startsWith('http') ? imgUrl : `${BACKEND_URL}${imgUrl}`);
        } else {
          setHeroImgSrc(DEFAULT_IMAGES.hero);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setHeroImgSrc(DEFAULT_IMAGES.hero);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const heroText = ui?.heroText || 'Illuminate Your World';
  const heroSubtext = ui?.heroSubtext || 'Premium lighting solutions for every occasion';

  const categories = [
    { name: 'Festival Lighting', image: ui?.categoryFestivalImage || DEFAULT_IMAGES.festival, description: 'Celebration & Festival Lights' },
    { name: 'Decorative Lighting', image: ui?.categoryDecorativeImage || DEFAULT_IMAGES.decorative, description: 'Home & Caf\u00e9 Ambiance' },
    { name: 'Flood & Industrial Lighting', image: ui?.categoryIndustrialImage || DEFAULT_IMAGES.industrial, description: 'High-Power Solutions' },
    { name: 'LED Panels', image: ui?.categoryLedImage || DEFAULT_IMAGES.led, description: 'Modern LED Panels' }
  ];

  const features = [
    { icon: Zap, title: ui?.featureText1 || 'Premium Quality', desc: ui?.featureDesc1 || 'Certified lighting products with warranty' },
    { icon: Truck, title: ui?.featureText2 || 'Fast Delivery', desc: ui?.featureDesc2 || 'Quick dispatch across India' },
    { icon: Shield, title: ui?.featureText3 || 'Secure Payments', desc: ui?.featureDesc3 || 'Safe and encrypted transactions' }
  ];

  const ctaTitle = ui?.ctaTitle || 'Need Bulk Orders?';
  const ctaSubtext = ui?.ctaSubtext || 'Special pricing for wholesale and bulk purchases. Contact us for custom quotes.';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A192F]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent mx-auto mb-4"></div>
          <p className="text-white/60 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[500px] md:h-[620px] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImgSrc})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/80 via-[#0A192F]/40 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-12 flex items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tighter" style={{fontFamily: 'Playfair Display, serif'}} data-testid="hero-title">{heroText}</h1>
            <p className="text-base md:text-lg text-white/90 mb-8 leading-relaxed">{heroSubtext}</p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={() => navigate('/products')} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] px-8 py-3 h-auto text-base" data-testid="hero-shop-btn">
                <ShoppingBag className="mr-2" size={20} /> Shop Now
              </Button>
              <Button onClick={() => navigate('/bulk-inquiry')} variant="outline" className="bg-white/10 backdrop-blur text-white hover:bg-white hover:text-[#0A192F] border-white/50 px-8 py-3 h-auto text-base" data-testid="hero-bulk-btn">
                Bulk Orders
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex items-start gap-4">
                <div className="bg-[#D4AF37] p-3 rounded-full flex-shrink-0"><f.icon size={24} className="text-white" /></div>
                <div><h3 className="font-bold text-lg mb-1">{f.title}</h3><p className="text-gray-600 text-sm">{f.desc}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{fontFamily: 'Playfair Display, serif'}}>Shop by Category</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <motion.div key={category.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/products?category=${encodeURIComponent(category.name)}`)}
                className="card card-hover cursor-pointer overflow-hidden group" data-testid={`category-card-${index}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-sm md:text-base mb-1">{category.name}</h3>
                  <p className="text-gray-600 text-xs md:text-sm">{category.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products — compact grid */}
      {!loading && featuredProducts.length > 0 && (
        <section className="py-20 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{fontFamily: 'Playfair Display, serif'}}>Featured Products</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {featuredProducts.map((product, index) => (
                <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/products/${product.id}`)} className="card card-hover cursor-pointer overflow-hidden" data-testid={`featured-product-${index}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="font-bold text-sm md:text-base mb-1 line-clamp-1">{product.name}</h3>
                    {ratings[product.id] && (
                      <div className="flex items-center gap-1 mb-1">
                        <StarRating rating={Math.round(ratings[product.id].averageRating)} size={12} />
                        <span className="text-[10px] text-gray-500">({ratings[product.id].totalReviews})</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-[#D4AF37] font-bold text-base md:text-lg">&#8377;{product.price}</span>
                      {product.originalPrice && product.originalPrice > product.price && <span className="text-gray-400 line-through text-xs">&#8377;{product.originalPrice}</span>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button onClick={() => navigate('/products')} variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-8 py-3 h-auto" data-testid="view-all-products-btn">
                View All Products
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#0A192F] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{fontFamily: 'Playfair Display, serif'}}>{ctaTitle}</h2>
          <p className="text-base text-white/90 mb-8 max-w-2xl mx-auto">{ctaSubtext}</p>
          <Button onClick={() => navigate('/bulk-inquiry')} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] px-8 py-3 h-auto text-base" data-testid="bulk-cta-btn">
            Get Bulk Pricing
          </Button>
        </div>
      </section>
    </div>
  );
};
